import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  Card,
  Flexbox,
  Pagination,
  SingleSelect
} from '@ibinder/common-frontend';
import { chevronDown, chevronRight } from '@ibinder/common-icons';
import {
  Table,
  TableContainer,
  TableData,
  TableHead,
  TableHeadContent,
  TableHeadItem,
  TableRow
} from '../ListDocument/styles.index';
import {
  AssignmentsTarget,
  BinderAccessNode,
  OrganizationAccessNode,
  RemovableResourceKind,
  TenderAccessNode,
  UserAccessGroups,
  resourceSelectionKey
} from './userResourceAccess.types';

type SelectedResource = {
  kind: RemovableResourceKind;
  id: string;
};

type Props = {
  userId: string;
  groups: UserAccessGroups;
  loading: boolean;
  selected: Set<string>;
  onSetSelected: (resources: SelectedResource[], isSelected: boolean) => void;
  onShowAssignments: (target: AssignmentsTarget) => void;
};

type SectionProps = {
  userId: string;
  title: string;
  description: string;
  organizations: OrganizationAccessNode[];
  selected: Set<string>;
  onSetSelected: (resources: SelectedResource[], isSelected: boolean) => void;
  onShowAssignments: (target: AssignmentsTarget) => void;
};

const PAGE_SIZES = [10, 20, 30, 40, 50];
const INDENT_STEP_REM = 1.25;
// Only organizations expand, so no deeper row needs chevron space reserved.
const MAX_CHEVRON_DEPTH = 0;

const PaginationContainer = styled(Flexbox)`
  align-items: center;
`;

const StyledSingleSelect = styled(SingleSelect)`
  margin-left: auto;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
`;

const SectionHeading = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1rem;
  margin: 0;
`;

const SectionDescription = styled.p`
  color: ${({ theme }) => theme.colors.discrete};
  margin: 0.25rem 0 0.75rem 0;
`;

const SectionDivider = styled.hr`
  background: ${({ theme }) => theme.colors.primary};
  border: 0;
  height: 0.25rem;
  margin: 2rem 0 1.5rem 0;
  width: 100%;
`;

const NameCell = styled(Flexbox)`
  align-items: center;
`;

const ExpanderCell = styled(TableData)<{ $depth: number }>`
  padding-left: ${({ $depth }) => 0.25 + $depth * INDENT_STEP_REM}rem;
  padding-right: ${({ $depth }) =>
    Math.max(MAX_CHEVRON_DEPTH - $depth, 0) * INDENT_STEP_REM}rem;
`;

// The name column carries the indentation for the levels below an
// organization; it has enough slack that indenting never widens it.
const NameCellData = styled(TableData)<{ $depth: number }>`
  padding-left: ${({ $depth }) => 0.75 + $depth * INDENT_STEP_REM}rem;
`;

const ExpandButton = styled.button`
  align-items: center;
  background: none;
  border: 0;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  display: inline-flex;
  padding: 0;

  svg {
    height: 1.5rem;
    min-width: 1.5rem;
    width: 1.5rem;
  }
`;

const AssignmentsCell = styled(TableData)`
  vertical-align: middle;

  && button {
    margin: 0;
  }
`;

const AssignmentsWrap = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  padding: 0.5rem 0.75rem 0.5rem 0;
`;

const AssignmentsButton = styled(Button)`
  && {
    border-radius: 0.375rem;
    flex: 0 0 auto;
    font-size: 0.75rem;
    height: auto;
    line-height: 1.25;
    margin: 0;
    min-height: 0;
    min-width: 0;
    padding: 0.25rem 0.625rem;
    white-space: nowrap;
    width: auto;
  }
`;

const NoDirectAccess = styled.span`
  color: ${({ theme }) => theme.colors.discrete};
  font-style: italic;
`;

const CellText = styled.span`
  white-space: nowrap;
`;

const SelectCell = styled(TableData)`
  text-align: center;
  width: 2.5rem;
`;

const SelectCheckbox = styled.input`
  cursor: pointer;
  height: 1rem;
  margin: 0;
  width: 1rem;

  &:disabled {
    cursor: default;
  }
`;

const columns = ['Type', 'Name', 'Id', 'Role', 'Assignments'];

const toggleSetValue = (values: Set<string>, id: string): Set<string> => {
  const next = new Set(values);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
};

const AccessSection = ({
  userId,
  title,
  description,
  organizations,
  selected,
  onSetSelected,
  onShowAssignments
}: SectionProps) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedOrganizations, setExpandedOrganizations] = useState<
    Set<string>
  >(new Set());
  const [selectionAnchor, setSelectionAnchor] = useState<string | null>(null);
  const shiftSelection = useRef(false);

  const pageCount = Math.max(1, Math.ceil(organizations.length / pageSize));
  const pageOptions = useMemo(
    () => Array.from({ length: pageCount }, (_, index) => index),
    [pageCount]
  );

  useEffect(() => {
    if (pageIndex > pageCount - 1) {
      setPageIndex(0);
    }
  }, [pageIndex, pageCount]);

  const pagedOrganizations = organizations.slice(
    pageIndex * pageSize,
    pageIndex * pageSize + pageSize
  );

  const changePageSize = (index: number) => {
    setActiveIndex(index);
    setPageSize(PAGE_SIZES[index]);
    setPageIndex(0);
  };

  const paginationControls = organizations.length > PAGE_SIZES[0] && (
    <PaginationContainer>
      <Pagination
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        canNextPage={pageIndex < pageCount - 1}
        canPreviousPage={pageIndex > 0}
        nextPage={() => setPageIndex((index) => index + 1)}
        previousPage={() => setPageIndex((index) => index - 1)}
        gotoPage={setPageIndex}
        previousText="Previous"
        nextText="Next"
      />
      <StyledSingleSelect
        label={undefined}
        dropdownText="Select.."
        activeIndex={activeIndex}
        dropdownData={PAGE_SIZES.map((size) => ({
          Key: size,
          Value: `Show ${size}`
        }))}
        onClickSelectItem={changePageSize}
        withSortButton={false}
      />
    </PaginationContainer>
  );

  const visibleSelectableResources = () => {
    const resources: SelectedResource[] = [];
    pagedOrganizations.forEach((organization) => {
      if (
        organization.hasDirectAccess &&
        organization.id !== userId &&
        !organization.isLastOwner
      ) {
        resources.push({ kind: 'organization', id: organization.id });
      }
      if (!expandedOrganizations.has(organization.id)) {
        return;
      }
      organization.binders.forEach((binder) => {
        if (!binder.isLastAdmin) {
          resources.push({ kind: 'binder', id: binder.id });
        }
      });
      organization.tenders.forEach((tender) => {
        if (!tender.isLastAdmin) {
          resources.push({ kind: 'tender', id: tender.id });
        }
      });
    });
    return resources;
  };

  const handleSelect = (
    kind: RemovableResourceKind,
    id: string,
    shiftKey: boolean
  ) => {
    const resources = visibleSelectableResources();
    const key = resourceSelectionKey(kind, id);
    const index = resources.findIndex(
      (resource) => resourceSelectionKey(resource.kind, resource.id) === key
    );
    if (index === -1) {
      return;
    }
    const anchorIndex =
      selectionAnchor === null
        ? -1
        : resources.findIndex(
            (resource) =>
              resourceSelectionKey(resource.kind, resource.id) ===
              selectionAnchor
          );
    const isSelected = !selected.has(key);
    if (shiftKey && anchorIndex !== -1) {
      const start = Math.min(anchorIndex, index);
      const end = Math.max(anchorIndex, index);
      onSetSelected(resources.slice(start, end + 1), isSelected);
      return;
    }
    onSetSelected([resources[index]], isSelected);
    setSelectionAnchor(key);
  };

  const selectCell = (
    kind: RemovableResourceKind,
    id: string,
    name: string,
    disabled = false
  ) => (
    <SelectCell>
      <SelectCheckbox
        type="checkbox"
        aria-label={`Select ${name}`}
        checked={!disabled && selected.has(resourceSelectionKey(kind, id))}
        disabled={disabled}
        onMouseDown={(event) => {
          if (!event.shiftKey) {
            shiftSelection.current = false;
            return;
          }
          event.preventDefault();
          shiftSelection.current = true;
          handleSelect(kind, id, true);
        }}
        onChange={() => {
          if (shiftSelection.current) {
            shiftSelection.current = false;
            return;
          }
          handleSelect(kind, id, false);
        }}
      />
    </SelectCell>
  );

  const assignmentsCell = (target: AssignmentsTarget) => (
    <AssignmentsCell>
      <AssignmentsWrap>
        <AssignmentsButton
          btnStyle="secondary"
          label="Assignments"
          onClick={() => onShowAssignments(target)}
        />
      </AssignmentsWrap>
    </AssignmentsCell>
  );

  const renderBinderRow = (
    binder: BinderAccessNode,
    organization: OrganizationAccessNode
  ) => (
    <TableRow key={`binder-${binder.id}`}>
      <ExpanderCell $depth={1} />
      {selectCell('binder', binder.id, binder.name, binder.isLastAdmin)}
      <TableData>
        <CellText>Binder</CellText>
      </TableData>
      <NameCellData $depth={1}>
        <NameCell>
          <CellText>{binder.name}</CellText>
        </NameCell>
      </NameCellData>
      <TableData>
        <CellText>{binder.id}</CellText>
      </TableData>
      <TableData>
        <CellText>
          {binder.isLastAdmin ? 'Last Admin' : binder.participantType}
        </CellText>
      </TableData>
      {assignmentsCell({
        kind: 'Binder',
        id: binder.id,
        name: binder.name,
        organizationId: organization.id,
        organizationName: organization.name,
        participantType: binder.participantType
      })}
    </TableRow>
  );

  const renderTenderRow = (tender: TenderAccessNode) => (
    <TableRow key={`tender-${tender.id}`}>
      <ExpanderCell $depth={1} />
      {selectCell('tender', tender.id, tender.name, tender.isLastAdmin)}
      <TableData>
        <CellText>Tender</CellText>
      </TableData>
      <NameCellData $depth={1}>
        <NameCell>
          <CellText>{tender.name}</CellText>
        </NameCell>
      </NameCellData>
      <TableData>
        <CellText>{tender.id}</CellText>
      </TableData>
      <TableData>
        <CellText>
          {tender.isLastAdmin ? 'Last Admin' : tender.participantType}
        </CellText>
      </TableData>
      <TableData />
    </TableRow>
  );

  const renderOrganization = (organization: OrganizationAccessNode) => {
    const isExpanded = expandedOrganizations.has(organization.id);
    const canExpand =
      organization.binders.length > 0 || organization.tenders.length > 0;

    return (
      <React.Fragment key={`organization-${organization.id}`}>
        <TableRow>
          <ExpanderCell $depth={0}>
            {canExpand && (
              <ExpandButton
                type="button"
                aria-expanded={isExpanded}
                aria-label={
                  isExpanded
                    ? `Collapse ${organization.name}`
                    : `Expand ${organization.name}`
                }
                onClick={() =>
                  setExpandedOrganizations((current) =>
                    toggleSetValue(current, organization.id)
                  )
                }
              >
                {isExpanded ? chevronDown : chevronRight}
              </ExpandButton>
            )}
          </ExpanderCell>
          {selectCell(
            'organization',
            organization.id,
            organization.name,
            !organization.hasDirectAccess ||
              organization.id === userId ||
              organization.isLastOwner
          )}
          <TableData>
            <CellText>Organization</CellText>
          </TableData>
          <NameCellData $depth={0}>
            <NameCell>
              <CellText>{organization.name}</CellText>
            </NameCell>
          </NameCellData>
          <TableData>
            <CellText>{organization.id}</CellText>
          </TableData>
          <TableData>
            {organization.hasDirectAccess ? (
              <CellText>
                {organization.isLastOwner
                  ? 'Last Owner'
                  : organization.participantType}
              </CellText>
            ) : (
              <NoDirectAccess>No direct access</NoDirectAccess>
            )}
          </TableData>
          {assignmentsCell({
            kind: 'Organization',
            id: organization.id,
            name: organization.name,
            organizationId: organization.id,
            organizationName: organization.name,
            participantType: organization.participantType
          })}
        </TableRow>
        {isExpanded &&
          organization.binders.map((binder) =>
            renderBinderRow(binder, organization)
          )}
        {isExpanded && organization.tenders.map(renderTenderRow)}
      </React.Fragment>
    );
  };

  return (
    <Section>
      <SectionHeading>{title}</SectionHeading>
      <SectionDescription>{description}</SectionDescription>
      {paginationControls}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeadItem />
              <TableHeadItem />
              {columns.map((column) => (
                <TableHeadItem key={column}>
                  <TableHeadContent>
                    <span>{column}</span>
                  </TableHeadContent>
                </TableHeadItem>
              ))}
            </TableRow>
          </TableHead>
          <tbody>{pagedOrganizations.map(renderOrganization)}</tbody>
        </Table>
      </TableContainer>
      {paginationControls}
    </Section>
  );
};

const UserResourceAccessTree = ({
  userId,
  groups,
  loading,
  selected,
  onSetSelected,
  onShowAssignments
}: Props) => {
  if (loading) {
    return (
      <div>
        {new Array(4).fill(true).map((_, index) => (
          <Card key={index} label={undefined} loading={true} />
        ))}
      </div>
    );
  }

  return (
    <>
      {groups.direct.length > 0 && (
        <AccessSection
          title="Direct organization participant"
          description="Organizations this user participates in, with the binders and tenders inside them."
          userId={userId}
          organizations={groups.direct}
          selected={selected}
          onSetSelected={onSetSelected}
          onShowAssignments={onShowAssignments}
        />
      )}
      {groups.direct.length > 0 && groups.indirect.length > 0 && (
        <SectionDivider />
      )}
      {groups.indirect.length > 0 && (
        <AccessSection
          title="Access through resources only"
          description="Organizations this user does not participate in, reached through a binder or tender inside them."
          userId={userId}
          organizations={groups.indirect}
          selected={selected}
          onSetSelected={onSetSelected}
          onShowAssignments={onShowAssignments}
        />
      )}
    </>
  );
};

export default UserResourceAccessTree;
